import React, { useState, useEffect, useRef } from 'react';

const DEFAULT_CATEGORIES = [
  { name: "Taxes/Set Aside",     pct: 25, color: "#E05C5C" },
  { name: "Housing",             pct: 20, color: "#C4952A" },
  { name: "Savings/Investments", pct: 15, color: "#1D9E75" },
  { name: "Food",                pct: 10, color: "#378ADD" },
  { name: "Transportation",      pct: 10, color: "#9B72CF" },
  { name: "Entertainment",       pct: 10, color: "#D4822A" },
  { name: "Miscellaneous",       pct: 10, color: "#7A7A8C" },
];

function DonutChart({ categories, weekly }) {
  const size = 220;
  const cx = size / 2, cy = size / 2;
  const r = 80, innerR = 52;
  const total = categories.reduce((s, c) => s + c.pct, 0) || 100;
  let angle = -90;
  const slices = categories.map(c => {
    const deg = (c.pct / total) * 360;
    const start = angle;
    angle += deg;
    return { ...c, start, deg };
  });

  function polarToXY(a, rad) {
    const rad2 = (a * Math.PI) / 180;
    return [cx + rad * Math.cos(rad2), cy + rad * Math.sin(rad2)];
  }

  function describeSlice(startDeg, deg, outerR, innerR2) {
    if (deg >= 360) deg = 359.99;
    const [x1, y1] = polarToXY(startDeg, outerR);
    const [x2, y2] = polarToXY(startDeg + deg, outerR);
    const [x3, y3] = polarToXY(startDeg + deg, innerR2);
    const [x4, y4] = polarToXY(startDeg, innerR2);
    const large = deg > 180 ? 1 : 0;
    return `M${x1},${y1} A${outerR},${outerR} 0 ${large} 1 ${x2},${y2} L${x3},${y3} A${innerR2},${innerR2} 0 ${large} 0 ${x4},${y4} Z`;
  }

  return (
    <svg viewBox={`0 0 ${size} ${size}`} style={{ width: "100%", maxWidth: 260, display: "block", margin: "0 auto" }}>
      {slices.map((s, i) => (
        <path key={i} d={describeSlice(s.start, s.deg, r, innerR)} fill={s.color} stroke="var(--bg2)" strokeWidth={2} />
      ))}
      <circle cx={cx} cy={cy} r={innerR - 2} fill="var(--bg2)" />
      <text x={cx} y={cy - 8} textAnchor="middle" fontSize={16} fontWeight={800} fill="var(--text)">${(weekly || 0).toLocaleString()}</text>
      <text x={cx} y={cy + 10} textAnchor="middle" fontSize={10} fill="var(--text3)">weekly</text>
    </svg>
  );
}

export default function BudgetTracker() {
  const [weekly, setWeekly] = useState('');
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [taxRate] = useState(25);

  const net = weekly ? Math.round(parseFloat(weekly) * (1 - taxRate / 100) * 100) / 100 : 0;
  const total = categories.reduce((s, c) => s + c.pct, 0);

  const updatePct = (i, val) => {
    const n = Math.max(0, Math.min(100, parseInt(val) || 0));
    setCategories(cats => cats.map((c, idx) => idx === i ? { ...c, pct: n } : c));
  };

  const dollarAmt = (pct) => weekly ? (parseFloat(weekly) * pct / 100).toFixed(2) : '0.00';

  const card = { background: "var(--bg2)", borderRadius: 16, border: "0.5px solid rgba(83,74,183,0.3)", overflow: "hidden", boxShadow: "0 0 30px rgba(83,74,183,0.08)" };
  const hdr = { padding: "14px 20px", borderBottom: "0.5px solid var(--border)", background: "linear-gradient(135deg,rgba(83,74,183,0.08),rgba(29,158,117,0.06))", display: "flex", alignItems: "center", gap: 10 };
  const dot = (color) => ({ width: 8, height: 8, borderRadius: "50%", background: color || "#534AB7", boxShadow: `0 0 8px ${color || "#534AB7"}`, flexShrink: 0 });
  const lbl = { fontSize: 11, fontWeight: 600, color: "var(--text2)", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.5px" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 680 }}>

      {/* Weekly Input */}
      <div style={card}>
        <div style={hdr}>
          <div style={dot("#534AB7")} />
          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>Weekly Check Input</span>
        </div>
        <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <div style={lbl}>Weekly Check Amount ($)</div>
            <input value={weekly} onChange={e => setWeekly(e.target.value)} type="number" placeholder="Enter weekly amount..." style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: "0.5px solid var(--border2)", background: "var(--bg3)", fontSize: 13, color: "var(--text)", boxSizing: "border-box" }} />
          </div>

          {weekly && (
            <div style={{ padding: "16px 20px", borderRadius: 12, background: "rgba(83,74,183,0.06)", border: "0.5px solid rgba(83,74,183,0.2)" }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Net after taxes ({taxRate}%)</div>
              <div style={{ fontSize: 32, fontWeight: 800, color: "#534AB7", lineHeight: 1 }}>${net.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
              <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>{taxRate}% of ${parseFloat(weekly).toLocaleString()}</div>
            </div>
          )}
        </div>
      </div>

      {/* Budget Split */}
      <div style={card}>
        <div style={hdr}>
          <div style={dot("#1D9E75")} />
          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>Adjust Percentages</span>
          <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 600, color: total === 100 ? "#1D9E75" : "#E05C5C" }}>{total}% total</span>
        </div>
        <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
          {categories.map((cat, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: cat.color, flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: 13, color: "var(--text)", fontWeight: 500 }}>{cat.name}</span>
              <input value={cat.pct} onChange={e => updatePct(i, e.target.value)} type="number" min="0" max="100"
                style={{ width: 60, padding: "6px 10px", borderRadius: 8, border: "0.5px solid var(--border2)", background: "var(--bg3)", fontSize: 13, color: "var(--text)", textAlign: "center" }} />
              <span style={{ fontSize: 12, color: "var(--text3)", width: 14 }}>%</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: cat.color, minWidth: 90, textAlign: "right" }}>${dollarAmt(cat.pct)}</span>
            </div>
          ))}
          {total !== 100 && (
            <div style={{ padding: "8px 12px", borderRadius: 8, background: "rgba(224,92,92,0.08)", border: "0.5px solid rgba(224,92,92,0.3)", fontSize: 12, color: "#E05C5C" }}>
              Percentages must add up to 100%. Currently at {total}%.
            </div>
          )}
        </div>
      </div>

      {/* Donut Chart */}
      <div style={card}>
        <div style={hdr}>
          <div style={dot("#D4822A")} />
          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>Budget Visualization</span>
        </div>
        <div style={{ padding: 24 }}>
          <DonutChart categories={categories} weekly={weekly ? parseFloat(weekly) : 0} />
          <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 8 }}>
            {categories.map((cat, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 10, height: 10, borderRadius: "50%", background: cat.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: "var(--text2)" }}>{cat.name} ({cat.pct}%)</span>
                </div>
                <span style={{ fontSize: 12, fontWeight: 600, color: cat.color }}>${dollarAmt(cat.pct)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}